import { Loader } from "lucide-react";
import { useEffect, useCallback, useMemo, useRef, useState } from "react";
import { PAYPHONE_SDK_JS, PAYPHONE_SDK_CSS, ROUTES } from "../../shared/constants";
import { logger } from "@shared/utils";
import { PluxButton } from "./PluxButton";
import type { PluxData } from "@/config/plux.data";

interface PaymentDisplayStepProps {
  paymentMethod: "PAYPHONE" | "DEUNA" | "JEP" | "COOPERCO" | "PLUX";
  isLoading: boolean;
  qrData?: string;
  numericCode?: string;
  expiresAt?: string;
  redirectUrl?: string;
  error?: string;
  // PayPhone SDK data
  payPhoneToken?: string;
  payPhoneClientTransactionId?: string;
  payPhoneAmount?: number;
  payPhoneAmountWithoutTax?: number;
  payPhoneTax?: number;
  payPhoneAmountWithTax?: number;
  payPhoneStoreId?: string;
  payPhoneReference?: string;
  // Plux (PagoPlux) — Botón de pagos (popup Paybox)
  pluxData?: PluxData | null;
}

/**
 * Checkout step 4 — payment display.
 * Shows QR for DeUna/JEP or embedded PayPhone form.
 * Matches the aesthetic of other steps.
 */
export function PaymentDisplayStep({
  paymentMethod,
  isLoading,
  qrData,
  numericCode,
  expiresAt,
  redirectUrl,
  error,
  payPhoneToken,
  payPhoneClientTransactionId,
  payPhoneAmount,
  payPhoneAmountWithoutTax,
  payPhoneTax,
  payPhoneAmountWithTax,
  payPhoneStoreId,
  payPhoneReference,
  pluxData,
}: PaymentDisplayStepProps) {
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);

  // Use useRef to store reference to div#pp-button without triggering re-renders
  const ppButtonRef = useRef<HTMLDivElement | null>(null);

  const ppButtonCallbackRef = useCallback((node: HTMLDivElement | null) => {
    ppButtonRef.current = node;
  }, []);

  // Initialize PayPhone SDK when component mounts and we have token
  useEffect(() => {
    if (paymentMethod !== "PAYPHONE" || !payPhoneToken || isLoading || error)
      return;

    const doRender = () => {
      if (!ppButtonRef.current || !window.PPaymentButtonBox) return;
      try {
        new window.PPaymentButtonBox({
          token: payPhoneToken,
          clientTransactionId: payPhoneClientTransactionId ?? "",
          amount: payPhoneAmount ?? 0,
          amountWithoutTax: payPhoneAmountWithoutTax ?? 0,
          currency: "USD",
          storeId: payPhoneStoreId ?? "",
          reference: payPhoneReference ?? "",
          // Sin estas dos URLs, PayPhone no tiene forma de avisar de inmediato
          // que el usuario canceló — el frontend quedaba 3 min reintentando a
          // ciegas antes de mostrar un mensaje ambiguo. Con cancellationUrl,
          // PayPhone redirige directo a /payment/cancel (pagina ya existente,
          // nunca conectada).
          responseUrl: `${window.location.origin}${ROUTES.PAYMENT_RETURN}`,
          cancellationUrl: `${window.location.origin}${ROUTES.PAYMENT_CANCEL}`,
        }).render("pp-button");
      } catch (err) {
        logger.error("Error initializing PayPhone button:", err);
      }
    };

    const scriptUrl = PAYPHONE_SDK_JS;
    const existingCss = document.querySelector(
      'link[href*="payphone-payment-box.css"]',
    );
    if (!existingCss) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = PAYPHONE_SDK_CSS;
      document.head.appendChild(link);
    }

    const existingOverride = document.querySelector(
      "style[data-payphone-override]",
    );
    if (!existingOverride) {
      const override = document.createElement("style");
      override.setAttribute("data-payphone-override", "true");
      override.textContent = `
    #root {
      max-width: unset !important;
      margin: unset !important;
      padding: unset !important;
      min-height: 100vh !important;
      display: flex !important;
      flex-direction: column !important;
    }
  `;
      document.head.appendChild(override);
    }

    const existingScript = document.querySelector(
      `script[src*="payphone-payment-box"]`,
    );

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = scriptUrl;
      script.async = true;
      script.onload = () => {
        const check = () => {
          if (window.PPaymentButtonBox) {
            setTimeout(doRender, 50);
          } else {
            requestAnimationFrame(check);
          }
        };
        check();
      };
      document.head.appendChild(script);
    } else {
      const check = () => {
        if (window.PPaymentButtonBox) {
          setTimeout(doRender, 50);
        } else {
          requestAnimationFrame(check);
        }
      };
      check();
    }
  }, [
    paymentMethod,
    payPhoneToken,
    payPhoneClientTransactionId,
    payPhoneAmount,
    payPhoneAmountWithoutTax,
    payPhoneTax,
    payPhoneAmountWithTax,
    payPhoneStoreId,
    payPhoneReference,
    isLoading,
    error,
  ]);

  const formattedNumericCode = useMemo(() => {
    if (!numericCode) return "";
    const digitsOnly = numericCode.replace(/\s+/g, "");
    if (digitsOnly.length === 6) {
      return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3)}`;
    }
    return digitsOnly;
  }, [numericCode]);

  useEffect(() => {
    if ((paymentMethod !== "DEUNA" && paymentMethod !== "COOPERCO") || !expiresAt) {
      const timeoutId = window.setTimeout(() => {
        setRemainingSeconds(null);
      }, 0);
      return () => window.clearTimeout(timeoutId);
    }
    const targetMs = new Date(expiresAt).getTime();
    if (Number.isNaN(targetMs)) {
      const timeoutId = window.setTimeout(() => {
        setRemainingSeconds(null);
      }, 0);
      return () => window.clearTimeout(timeoutId);
    }

    const updateRemaining = () => {
      const diffSeconds = Math.max(0, Math.ceil((targetMs - Date.now()) / 1000));
      setRemainingSeconds(diffSeconds);
    };

    const initialTimeoutId = window.setTimeout(updateRemaining, 0);
    const intervalId = window.setInterval(updateRemaining, 1000);
    return () => {
      window.clearTimeout(initialTimeoutId);
      window.clearInterval(intervalId);
    };
  }, [paymentMethod, expiresAt]);

  const remainingLabel = useMemo(() => {
    if (remainingSeconds === null) return "";
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  }, [remainingSeconds]);

  return (
    <div className="flex flex-col gap-4">
      <span className="font-semibold text-black" style={{ fontSize: 20 }}>
        {paymentMethod === "PAYPHONE"
          ? "Completa tu pago con PayPhone"
          : paymentMethod === "DEUNA"
            ? "Completa tu pago con DeUna"
            : paymentMethod === "COOPERCO"
              ? "Completa tu pago con Cooperco"
              : paymentMethod === "PLUX"
                ? "Completa tu pago con Plux"
                : "Completa tu pago con JEP"}
      </span>

      {isLoading && (
        <div className="flex flex-col items-center justify-center gap-4 py-8">
          <Loader className="animate-spin text-primary-110" size={32} />
          <span className="text-center text-gray-600">
            Generando{" "}
            {paymentMethod === "DEUNA"
              ? "QR y código"
              : "información"}{" "}
            de pago...
          </span>
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-100 px-4 py-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      {!isLoading && !error && (
        <>
          {paymentMethod === "DEUNA" && (numericCode || qrData) && (
            <div className="flex flex-col items-center gap-4">
              {numericCode && (
                <div className="flex flex-col items-center gap-2">
                  <p className="text-center text-gray-600 text-sm">
                    Ingresa este código en tu app De Una
                  </p>
                  <div className="rounded-lg border border-primary-110 bg-secondary-120/10 px-6 py-3">
                    <span className="text-3xl font-bold tracking-widest text-primary-110">
                      {formattedNumericCode}
                    </span>
                  </div>
                  {remainingLabel && (
                    <span className="text-xs text-gray-500">
                      Expira en {remainingLabel}
                    </span>
                  )}
                </div>
              )}
              {qrData && (
                <>
                  <p className="text-center text-gray-600 text-sm">
                    O escanea este código QR con tu app De Una
                  </p>
                  <img
                    src={qrData}
                    alt="QR DeUna"
                    className="h-48 w-48 border border-gray-300 rounded-lg p-2"
                  />
                </>
              )}
              {redirectUrl && (
                <p className="text-center text-sm text-gray-500">
                  O puedes{" "}
                  <a
                    href={redirectUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-110 font-semibold hover:underline"
                  >
                    abrir tu app aquí
                  </a>
                </p>
              )}
            </div>
          )}

          {paymentMethod === "JEP" && qrData && (
            <div className="flex flex-col items-center gap-4">
              <p className="text-center text-gray-600 text-sm">
                Escanea este código QR con tu app JEPFASTER
              </p>
              <img
                src={qrData}
                alt="QR JEP"
                className="h-48 w-48 border border-gray-300 rounded-lg p-2"
              />
              {redirectUrl && (
                <p className="text-center text-sm text-gray-500">
                  O puedes{" "}
                  <a
                    href={redirectUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-110 font-semibold hover:underline"
                  >
                    abrir tu app aquí
                  </a>
                </p>
              )}
            </div>
          )}

          {paymentMethod === "COOPERCO" && qrData && (
            <div className="flex flex-col items-center gap-4">
              <p className="text-center text-gray-600 text-sm">
                Escanea este código QR con tu app de Cooperco
              </p>
              <img
                src={qrData}
                alt="QR Cooperco"
                className="h-48 w-48 border border-gray-300 rounded-lg p-2"
              />
              {remainingLabel && (
                <span className="text-xs text-gray-500">
                  Expira en {remainingLabel}
                </span>
              )}
            </div>
          )}

          {paymentMethod === "PAYPHONE" && payPhoneToken && (
            <div className="flex flex-col items-center gap-4">
              {/* PayPhone SDK renders here */}
              <div
                ref={ppButtonCallbackRef}
                id="pp-button"
                className="w-full"
                style={{ minHeight: "400px" }}
              />
            </div>
          )}

          {paymentMethod === "PLUX" && pluxData && (
            <div className="flex flex-col items-center gap-4">
              <p className="text-center text-gray-600 text-sm">
                Presiona el botón para pagar con tarjeta a través de Plux
              </p>
              <PluxButton data={pluxData} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Extend window type for PayPhone SDK
declare global {
  interface Window {
    PPaymentButtonBox?: new (config: {
      token: string;
      clientTransactionId: string;
      amount: number;
      amountWithoutTax: number;
      tax?: number;
      amountWithTax?: number;
      currency: string;
      storeId: string;
      reference: string;
      responseUrl?: string;
      cancellationUrl?: string;
    }) => {
      render: (elementId: string) => void;
    };
  }
}
